import React from 'react';
import {
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  Storage as StorageIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import InlineAlert from '../../../shared/components/InlineAlert';
import type { CustomerKBDocument, CustomerKBSyncJob } from '../services/customerKBService';

export interface CustomerKBContentProps {
  knowledgeBaseId: string | undefined;
  knowledgeBaseBucket: string | undefined;
  isKBConfigured: boolean;
  documents: CustomerKBDocument[];
  totalSize: number;
  loading: boolean;
  uploading: boolean;
  syncing: boolean;
  error: string | null;
  successMessage: string | null;
  currentSyncJob: CustomerKBSyncJob | null;
  onUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDownload: (doc: CustomerKBDocument) => void;
  onDelete: (doc: CustomerKBDocument) => void;
  onSync: () => void;
  onClearError: () => void;
  onClearSuccess: () => void;
  formatBytes: (bytes: number) => string;
  formatDate: (dateString: string) => string;
}

const CustomerKBContent: React.FC<CustomerKBContentProps> = ({
  knowledgeBaseId,
  knowledgeBaseBucket,
  isKBConfigured,
  documents,
  totalSize,
  loading,
  uploading,
  syncing,
  error,
  successMessage,
  currentSyncJob,
  onUpload,
  onDownload,
  onDelete,
  onSync,
  onClearError,
  onClearSuccess,
  formatBytes,
  formatDate,
}) => (
  <>
    <Box sx={{ p: 2, bgcolor: 'background.default', borderBottom: 1, borderColor: 'divider' }}>
      {isKBConfigured ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip icon={<CheckCircleIcon />} label="KB Configured" color="success" size="small" />
          <Typography variant="body2" color="text.secondary">
            KB ID: {knowledgeBaseId}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            S3 Location: {knowledgeBaseBucket}
          </Typography>
        </Box>
      ) : (
        <InlineAlert severity="warning" icon={<WarningIcon />}>
          <Typography variant="body2">
            This customer does not have a Knowledge Base configured.
            Configure KB ID and Source Bucket in Customer Settings (provisioned by LZA in the operations account).
          </Typography>
        </InlineAlert>
      )}
    </Box>

    {isKBConfigured && (
      <>
        {error && (
          <InlineAlert severity="error" onClose={onClearError} sx={{ m: 2, mb: 0 }}>
            {error}
          </InlineAlert>
        )}
        {successMessage && (
          <InlineAlert severity="success" onClose={onClearSuccess} sx={{ m: 2, mb: 0 }}>
            {successMessage}
          </InlineAlert>
        )}

        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            component="label"
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
            disabled={uploading}
          >
            Upload Document
            <input type="file" hidden onChange={onUpload} accept=".pdf,.txt,.md,.doc,.docx,.html" />
          </Button>

          <Button
            variant="outlined"
            startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={onSync}
            disabled={syncing}
          >
            Sync Knowledge Base
          </Button>

          <Box sx={{ flexGrow: 1 }} />

          <Typography variant="body2" color="text.secondary">
            {documents.length} documents ({formatBytes(totalSize)})
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : documents.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <StorageIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No documents yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Upload documents to build your customer's knowledge base
              </Typography>
            </Box>
          ) : (
            <List>
              {documents.map((doc) => (
                <ListItem
                  key={doc.document_id}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1, '&:hover': { bgcolor: 'action.hover' } }}
                >
                  <ListItemText
                    primary={doc.filename}
                    secondary={
                      <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">{formatBytes(doc.size_bytes)}</Typography>
                        <Typography variant="caption" color="text.secondary">{formatDate(doc.last_modified)}</Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => onDownload(doc)} title="Download" sx={{ mr: 1 }}>
                      <DownloadIcon />
                    </IconButton>
                    <IconButton edge="end" onClick={() => onDelete(doc)} title="Delete" color="error">
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {currentSyncJob && (
          <Box sx={{ p: 2, bgcolor: 'background.default', borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              Last Sync: Job {currentSyncJob.job_id} - Status: {currentSyncJob.status}
            </Typography>
          </Box>
        )}
      </>
    )}
  </>
);

export default CustomerKBContent;
